import React, { useState, useEffect, useCallback } from 'react';
import { Utensils, TrendingUp, XCircle, Users, Wifi, WifiOff, RefreshCw, Loader2, Clock, ChefHat } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

interface Statistics {
  totalToday: number;
  monthlyTotal: number;
  guestCount: number;
  unauthorizedToday: number;
  onlineDeviceCount: number;
  offlineDeviceCount: number;
  todayByPeriod?: Record<string, number>;
}
interface Period {
  periodID: number;
  periodName: string;
  startTime: string;
  endTime: string;
  maxPerPerson: number;
  price: number;
  isActive: boolean;
}
interface MenuRow {
  menuID: number;
  menuName: string;
  description?: string;
  periodID?: number | null;
  menuDate: string;
}
interface TxRow {
  transactionID?: number;
  employeeName?: string;
  employeeCode?: string;
  periodName?: string;
  transactionTime?: string;
  isAuthorized?: boolean;
}

const fmtTime = (t?: string) => {
  if (!t) return '—';
  const m = String(t).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : String(t).substring(0, 5);
};

const MealDashboard: React.FC = () => {
  const [stats, setStats] = useState<Statistics>({ totalToday: 0, monthlyTotal: 0, guestCount: 0, unauthorizedToday: 0, onlineDeviceCount: 0, offlineDeviceCount: 0 });
  const [periods, setPeriods] = useState<Period[]>([]);
  const [menus, setMenus] = useState<MenuRow[]>([]);
  const [transactions, setTransactions] = useState<TxRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r: any = await api.get('/meals/dashboard');
      const raw = r?.data ?? r;
      const data = raw?.data ?? raw;
      setStats(data?.statistics ?? { totalToday: 0, monthlyTotal: 0, guestCount: 0, unauthorizedToday: 0, onlineDeviceCount: 0, offlineDeviceCount: 0 });
      const p = data?.periods;
      setPeriods(Array.isArray(p) ? p : []);
      const m = data?.todayMenus;
      setMenus(Array.isArray(m) ? m : []);
      const t = data?.todayTransactions;
      setTransactions(Array.isArray(t) ? t : []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Dashboard yüklenemedi');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 md:p-8 space-y-6">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-6 text-white flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
            <Utensils size={26} /> Yemekhane Dashboard
          </h1>
          <p className="text-sm text-cyan-100 mt-1">Günlük geçiş özeti, öğün bazlı istatistikler ve canlı durum</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-bold backdrop-blur-sm disabled:opacity-60">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Yenile
          </button>
          <span className="inline-flex items-center gap-1 text-xs font-bold bg-emerald-500/90 px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> Canlı
          </span>
        </div>
      </div>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="BUGÜN TOPLAM" value={stats.totalToday} color="amber" icon={<Utensils size={16} />} />
        <StatCard label="BU AY" value={stats.monthlyTotal} color="blue" icon={<TrendingUp size={16} />} />
        <StatCard label="İZİNSİZ" value={stats.unauthorizedToday} color="red" icon={<XCircle size={16} />} />
        <StatCard label="MİSAFİR" value={stats.guestCount} color="purple" icon={<Users size={16} />} />
        <StatCard label="ONLİNE CİHAZ" value={stats.onlineDeviceCount} color="emerald" icon={<Wifi size={16} />} />
        <StatCard label="OFFLİNE CİHAZ" value={stats.offlineDeviceCount} color="slate" icon={<WifiOff size={16} />} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-base font-black text-slate-800 mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-amber-600" /> Bugün Öğün Bazlı Geçişler
          </h2>
          {Object.keys(stats.todayByPeriod ?? {}).length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <Utensils size={48} className="mx-auto opacity-30 mb-3" />
              <p className="text-sm">Bugün geçiş yok</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(stats.todayByPeriod ?? {}).map(([k, v]) => {
                const max = Math.max(...Object.values(stats.todayByPeriod ?? { z: 1 }));
                const pct = max > 0 ? (Number(v) / max) * 100 : 0;
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-bold text-slate-700">{k}</span>
                      <span className="text-slate-500">{v} geçiş</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-base font-black text-slate-800 mb-4 flex items-center gap-2">
            <Clock size={18} className="text-blue-600" /> Aktif Öğün Tanımları
          </h2>
          {periods.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">Tanımlı öğün yok</div>
          ) : (
            <ul className="space-y-3">
              {periods.map(p => (
                <li key={p.periodID} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0">
                  <div>
                    <p className="font-bold text-slate-800 text-sm">{p.periodName}</p>
                    <p className="text-[11px] text-slate-500">{fmtTime(p.startTime)} — {fmtTime(p.endTime)}</p>
                  </div>
                  <span className="inline-flex px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">Max {p.maxPerPerson}</span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/meal-periods" className="block mt-4 text-xs text-blue-600 hover:underline font-bold">
            Tüm Öğünleri Yönet →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-base font-black text-slate-800 mb-4 flex items-center gap-2">
            <ChefHat size={18} className="text-orange-600" /> Bugün Menü
          </h2>
          {menus.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              <ChefHat size={36} className="mx-auto opacity-30 mb-2" /> Bugün menü tanımlı değil
            </div>
          ) : (
            <ul className="space-y-2">
              {menus.map(m => (
                <li key={m.menuID} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                  <p className="font-bold text-slate-800">{m.menuName}</p>
                  {m.description && <p className="text-xs text-slate-500 mt-0.5">{m.description}</p>}
                </li>
              ))}
            </ul>
          )}
          <Link to="/meal-menus" className="block mt-4 text-xs text-orange-600 hover:underline font-bold">Menüleri Yönet →</Link>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="text-base font-black text-slate-800 mb-4 flex items-center gap-2">
            <Utensils size={18} className="text-emerald-600" /> Son Geçişler
          </h2>
          {transactions.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm">
              <Utensils size={36} className="mx-auto opacity-30 mb-2" /> Henüz geçiş yok
            </div>
          ) : (
            <ul className="space-y-2 max-h-64 overflow-y-auto">
              {transactions.slice(0, 8).map((t, i) => (
                <li key={t.transactionID ?? i} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                  <div>
                    <p className="font-bold text-slate-800">{t.employeeName ?? '—'}</p>
                    <p className="text-[11px] text-slate-500">#{t.employeeCode ?? '—'} · {t.periodName ?? '—'}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.isAuthorized ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {fmtTime(t.transactionTime)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link to="/meal-transactions" className="block mt-4 text-xs text-emerald-600 hover:underline font-bold">Tüm Hareketler →</Link>
        </div>
      </div>

      {loading && (
        <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-lg text-sm text-slate-600">
          <Loader2 size={14} className="animate-spin" /> Yükleniyor…
        </div>
      )}
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: string; icon: React.ReactNode }> = ({ label, value, color, icon }) => {
  const map: Record<string, { bg: string; border: string; text: string }> = {
    amber:   { bg: 'bg-amber-50',   border: 'border-l-amber-400',   text: 'text-amber-700' },
    blue:    { bg: 'bg-blue-50',    border: 'border-l-blue-400',    text: 'text-blue-700' },
    red:     { bg: 'bg-red-50',     border: 'border-l-red-400',     text: 'text-red-700' },
    purple:  { bg: 'bg-purple-50',  border: 'border-l-purple-400',  text: 'text-purple-700' },
    emerald: { bg: 'bg-emerald-50', border: 'border-l-emerald-400', text: 'text-emerald-700' },
    slate:   { bg: 'bg-slate-50',   border: 'border-l-slate-400',   text: 'text-slate-700' },
  };
  const c = map[color] ?? map.amber;
  return (
    <div className={`border border-slate-200 border-l-4 rounded-xl p-4 ${c.bg} ${c.border}`}>
      <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${c.text}`}>
        {icon} {label}
      </div>
      <p className="text-2xl font-black text-slate-800 mt-1">{value}</p>
    </div>
  );
};

export default MealDashboard;
